import { useState } from "react";
import { motion } from "framer-motion";
import { Landmark, QrCode, Lightbulb, ShieldCheck, Plane, FileText, HandCoins, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../lib/SiteContext";
import { Reveal, Overline } from "./Reveal";
import { scrollToId } from "./SmoothScroll";

const META = {
  banking: [Landmark, "/img/shop1.jpg"],
  payments: [QrCode, "/img/woman1.jpg"],
  bbps: [Lightbulb, "/img/store.jpg"],
  insurance: [ShieldCheck, "/img/woman2.jpg"],
  travel: [Plane, "/img/shop2.jpg"],
  essential: [FileText, "/img/woman2.jpg"],
  credit: [HandCoins, "/img/store.jpg"],
};

export const Services = () => {
  const { t } = useLang();
  const { settings } = useSite();
  const keys = Object.keys(META).filter((k) => settings.services_enabled.includes(k));
  const [active, setActive] = useState("banking");
  const cur = keys.includes(active) ? active : keys[0];
  if (!cur) return null;
  const s = t.services.items[cur];
  const [Icon, img] = META[cur];

  return (
    <section id="services" className="py-24 lg:py-36" data-testid="services-explorer">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="max-w-3xl">
          <Overline>{t.services.over}</Overline>
          <h2 className="section-title mt-5">{t.services.title}</h2>
          <p className="mt-5 text-base text-slate-300 md:text-lg">{t.services.sub}</p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 lg:col-span-4 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0">
            {keys.map((k, i) => {
              const [I] = META[k];
              const on = k === cur;
              return (
                <button
                  key={k}
                  onClick={() => setActive(k)}
                  data-testid={`service-tab-${k}`}
                  className={`group flex shrink-0 items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-[background-color,border-color] duration-300 ${on ? "border-saffron/60 bg-saffron/10" : "border-white/10 hover:border-white/25 hover:bg-white/[0.03]"}`}
                >
                  <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
                  <I size={20} className={on ? "text-saffron" : "text-slate-300"} />
                  <span className={`font-display text-base font-semibold ${on ? "text-white" : "text-slate-300"}`}>{t.services.items[k].t}</span>
                </button>
              );
            })}
          </div>

          <motion.div
            key={cur}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="grid overflow-hidden rounded-[28px] border border-white/10 bg-card md:grid-cols-2 lg:col-span-8"
            data-testid="service-panel"
          >
            <div className="relative h-64 overflow-hidden md:h-auto">
              <motion.img initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} src={img} alt={s.t} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-card" />
              <span className="absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-saffron text-ink"><Icon size={22} /></span>
            </div>
            <div className="flex flex-col p-7 lg:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-saffron">{s.t}</p>
              <h3 className="mt-4 font-display text-2xl font-bold leading-tight text-white lg:text-3xl" data-testid="service-panel-title">{s.h}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">{s.d}</p>
              <ul className="mt-6 grid grid-cols-2 gap-3">
                {s.f.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-200"><Check size={15} className="shrink-0 text-emerald" />{f}</li>
                ))}
              </ul>
              <button onClick={() => scrollToId("join")} className="btn-primary mt-8 self-start" data-testid="service-join-btn">
                {t.services.join} <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
